import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import DatePicker, {
  useDefaultStyles,
  type DateType,
} from 'react-native-ui-datepicker';
import { Colors } from '../constants/Colors';
import { useDynamicStyles } from '../hooks/useDynamicStyles';
import { TextReg } from './StyledText';
import { TextInput } from './TextInput';

// Generate 30-minute increment time options in 12-hour format
const generateTimeOptions = (): {
  label: string;
  value: { hours: number; minutes: number };
}[] => {
  const options: {
    label: string;
    value: { hours: number; minutes: number };
  }[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const meridiem = hour < 12 ? 'AM' : 'PM';
      const minuteStr = minute.toString().padStart(2, '0');
      options.push({
        label: `${displayHour}:${minuteStr} ${meridiem}`,
        value: { hours: hour, minutes: minute },
      });
    }
  }
  return options;
};

// Pre-generate time options once
const TIME_OPTIONS = generateTimeOptions();

export type DateSelectorMode = 'date' | 'datetime';

export type DateSelectorProps = {
  label: string;
  showLabel?: boolean;
  mode: DateSelectorMode;
  value?: Date;
  onChange: (date: Date) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  minimumDate?: Date;
  maximumDate?: Date;
  initialMonth?: Date; // For opening picker to a specific month
  style?: object;
  testID?: string;
};

export const DateSelector = ({
  label,
  showLabel = true,
  value,
  onChange,
  mode = 'date',
  placeholder,
  error,
  disabled = false,
  style,
  minimumDate,
  maximumDate,
  initialMonth,
  testID,
}: DateSelectorProps) => {
  const styles = useDynamicStyles(createStyles);
  const defaultStyles = useDefaultStyles();
  const [show, setShow] = useState(false);
  const animatedOpacity = useRef(new Animated.Value(0)).current;
  const [timeText, setTimeText] = useState<string>('');
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const timeDropdownRef = useRef<ScrollView>(null);

  // Format display value - only show when there's an actual value
  const formatDisplayValue = (): string => {
    if (!value) return '';
    if (mode === 'datetime') {
      return (
        value.toLocaleDateString() +
        ' ' +
        value.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      );
    }
    return value.toLocaleDateString();
  };

  const handleDateChange = (params: { date: DateType }) => {
    // react-native-ui-datepicker returns { date } for single date
    if (params.date) {
      // Handle different DateType formats (string, number, Date, or Dayjs)
      let dateValue: Date;
      if (params.date instanceof Date) {
        dateValue = params.date;
      } else if (
        typeof params.date === 'string' ||
        typeof params.date === 'number'
      ) {
        dateValue = new Date(params.date);
      } else {
        // Assume it's a Dayjs object with toDate() method
        dateValue = (params.date as { toDate: () => Date }).toDate();
      }
      // On web in datetime mode, preserve existing time when selecting a date
      if (Platform.OS === 'web' && mode === 'datetime' && value) {
        const preserved = new Date(dateValue);
        preserved.setHours(value.getHours(), value.getMinutes(), 0, 0);
        onChange(preserved);
      } else {
        onChange(dateValue);
      }
      // Auto-close after selection with animation
      // Keep open on web when in datetime mode so user can input time
      if (!(Platform.OS === 'web' && mode === 'datetime')) {
        hidePickerWithAnimation();
      }
    }
  };

  const showPickerWithAnimation = () => {
    setShow(true);
    Animated.timing(animatedOpacity, {
      toValue: 1,
      duration: 200,
      useNativeDriver: true,
    }).start();
  };

  const hidePickerWithAnimation = () => {
    Animated.timing(animatedOpacity, {
      toValue: 0,
      duration: 150,
      useNativeDriver: true,
    }).start(() => setShow(false));
  };

  const handlePress = () => {
    if (disabled) return;
    if (show) {
      hidePickerWithAnimation();
    } else {
      showPickerWithAnimation();
    }
  };

  // Reset animation values when show changes
  useEffect(() => {
    if (!show) {
      animatedOpacity.setValue(0);
    }
  }, [show]);

  const displayValue = formatDisplayValue();

  // Keep time text in sync with provided value
  useEffect(() => {
    if (value) {
      const hours = value.getHours();
      const minutes = value.getMinutes();
      const hh = hours.toString().padStart(2, '0');
      const mm = minutes.toString().padStart(2, '0');
      setTimeText(`${hh}:${mm}`);
    } else {
      setTimeText('');
    }
  }, [value]);

  const handleTimeChangeWeb = (text: string) => {
    setTimeText(text);
    // Accept HH:MM or H:MM, optionally with am/pm (we convert to 24h)
    const match = text
      .trim()
      .match(/^\s*(\d{1,2}):(\d{2})\s*(am|pm|AM|PM)?\s*$/);
    if (!match) return;
    let hours = parseInt(match[1] ?? '0', 10);
    const minutes = parseInt(match[2] ?? '0', 10);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return;
    if (minutes < 0 || minutes > 59) return;
    const meridiem = (match[3] ?? '').toLowerCase();
    if (meridiem === 'am' || meridiem === 'pm') {
      // Normalize 12h input
      hours = hours % 12;
      if (meridiem === 'pm') hours += 12;
    }
    if (hours < 0 || hours > 23) return;

    const base = value ? new Date(value) : new Date();
    base.setHours(hours, minutes, 0, 0);
    onChange(base);
  };

  const handleTimeSelect = (hours: number, minutes: number) => {
    const base = value ? new Date(value) : new Date();
    base.setHours(hours, minutes, 0, 0);
    onChange(base);
    setShowTimeDropdown(false);
    // Close the entire date/time picker
    hidePickerWithAnimation();
  };

  return (
    <View style={[styles.container, style]}>
      <Pressable onPress={handlePress} disabled={disabled} testID={testID}>
        <TextInput
          label={label}
          showLabel={showLabel}
          value={displayValue}
          placeholder={placeholder ?? `Select ${mode}`}
          editable={false}
          pointerEvents="none"
          error={!!error}
          style={[
            styles.input,
            disabled && styles.disabled,
            error && styles.errorInput,
          ]}
        />
      </Pressable>

      {show && (
        <Animated.View
          style={[
            styles.pickerContainer,
            {
              opacity: animatedOpacity,
            },
          ]}
        >
          <DatePicker
            date={value || initialMonth}
            mode="single"
            timePicker={Platform.OS === 'web' ? false : mode === 'datetime'}
            initialView="day"
            onChange={handleDateChange}
            minDate={minimumDate}
            maxDate={maximumDate}
            styles={{
              ...defaultStyles,
              // Ensure all labels are visible on dark backgrounds
              // Header labels
              month_selector_label: { color: Colors.text },
              year_selector_label: { color: Colors.text },
              weekday_label: { color: Colors.text },
              // Grid labels
              day_label: { color: Colors.text },
              month_label: { color: Colors.text },
              year_label: { color: Colors.text },
              // Purple theme configuration
              selected: {
                backgroundColor: Colors.purple1,
                borderRadius: 8,
              },
              selected_label: {
                color: Colors.text,
                fontWeight: '600',
              },
              today: {
                borderColor: Colors.purple1,
                borderWidth: 1,
              },
              today_label: {
                color: Colors.purple1,
                fontWeight: '600',
              },
            }}
            components={{
              IconPrev: (
                <TextReg
                  style={{
                    color: Colors.purple1,
                    fontSize: 18,
                    fontWeight: '600',
                  }}
                >
                  ‹
                </TextReg>
              ),
              IconNext: (
                <TextReg
                  style={{
                    color: Colors.purple1,
                    fontSize: 18,
                    fontWeight: '600',
                  }}
                >
                  ›
                </TextReg>
              ),
            }}
          />

          {Platform.OS === 'web' && mode === 'datetime' && (
            <View style={styles.timeContainer}>
              <TextReg style={styles.timeLabel}>Time</TextReg>
              <View style={styles.timeInputWrapper}>
                <TextInput
                  label="Time"
                  value={timeText}
                  onChange={handleTimeChangeWeb}
                  placeholder="HH:MM AM/PM"
                  testID={`${testID ?? 'date-selector'}-time-input`}
                  style={styles.timeInput}
                  onFocus={() => setShowTimeDropdown(true)}
                  onBlur={() => {
                    // Delay to allow time option click to register
                    setTimeout(() => setShowTimeDropdown(false), 150);
                  }}
                />
              </View>
              {showTimeDropdown && (
                <ScrollView
                  ref={timeDropdownRef}
                  style={styles.timeDropdown}
                  onLayout={() => {
                    // Auto-scroll to selected time or current time
                    const targetDate = value || new Date();
                    const targetHours = targetDate.getHours();
                    const targetMinutes = targetDate.getMinutes();

                    // Find the closest 30-minute increment
                    const roundedMinutes = Math.round(targetMinutes / 30) * 30;
                    const adjustedMinutes =
                      roundedMinutes === 60 ? 0 : roundedMinutes;
                    const adjustedHours =
                      roundedMinutes === 60
                        ? (targetHours + 1) % 24
                        : targetHours;

                    // Calculate index (48 total options: 24 hours * 2 for 30-min increments)
                    const targetIndex =
                      adjustedHours * 2 + (adjustedMinutes === 30 ? 1 : 0);
                    const itemHeight = 45; // padding (12) + text height + border
                    const scrollPosition = targetIndex * itemHeight;

                    // Scroll with a slight delay to ensure layout is complete
                    setTimeout(() => {
                      timeDropdownRef.current?.scrollTo({
                        y: scrollPosition,
                        animated: false,
                      });
                    }, 50);
                  }}
                >
                  {TIME_OPTIONS.map((option, index) => {
                    const { hours, minutes } = option.value;
                    const isSelected =
                      value &&
                      value.getHours() === hours &&
                      value.getMinutes() === minutes;

                    return (
                      <Pressable
                        key={index}
                        style={[
                          styles.timeOption,
                          isSelected && styles.timeOptionSelected,
                        ]}
                        onPress={() => handleTimeSelect(hours, minutes)}
                      >
                        <TextReg
                          style={[
                            styles.timeOptionText,
                            isSelected && styles.timeOptionTextSelected,
                          ]}
                        >
                          {option.label}
                        </TextReg>
                      </Pressable>
                    );
                  })}
                </ScrollView>
              )}
            </View>
          )}
        </Animated.View>
      )}
    </View>
  );
};

const createStyles = () =>
  StyleSheet.create({
    container: {
      width: '100%',
    },
    input: {
      backgroundColor: Colors.inputGrey,
    },
    disabled: {
      opacity: 0.5,
    },
    errorInput: {
      borderColor: Colors.pink0,
    },
    pickerContainer: {
      backgroundColor: Colors.purple0,
      borderRadius: 12,
      padding: 16,
      marginTop: 8,
      borderWidth: 1,
      borderColor: Colors.opacity10,
      shadowColor: Colors.purple0,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 6,
    },
    timeContainer: {
      marginTop: 12,
    },
    timeLabel: {
      marginBottom: 8,
    },
    timeInputWrapper: {
      position: 'relative',
    },
    timeInput: {
      backgroundColor: Colors.inputGrey,
    },
    timeDropdown: {
      backgroundColor: Colors.inputGrey,
      borderRadius: 8,
      marginTop: 4,
      maxHeight: 200,
      overflow: 'scroll',
      borderWidth: 1,
      borderColor: Colors.opacity10,
    },
    timeOption: {
      padding: 12,
      borderBottomWidth: 1,
      borderBottomColor: Colors.opacity10,
    },
    timeOptionSelected: {
      backgroundColor: Colors.pink0,
    },
    timeOptionText: {
      fontSize: 14,
    },
    timeOptionTextSelected: {
      color: Colors.text,
      fontWeight: '600',
    },
  });
